import { createContext, useContext, useState, useEffect } from "react";
import { publicApi, authenticatedApi } from "../lib/api";
import { updateManifest, cleanupManifest } from "../lib/manifest";
import { updateFaviconWithEventLogoOrText, resetFavicon } from "../lib/favicon";

const EventContext = createContext();

export function EventProvider({ children }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manifestURL, setManifestURL] = useState(null);

  // Get event slug from current URL
  const getEventSlug = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    return pathParts[1]; // /e/:eventSlug format
  };

  // Check if user is authenticated for current event
  const isAuthenticated = () => {
    const eventSlug = getEventSlug();
    if (!eventSlug) return false;

    const sessionKey = `attendee_session_${eventSlug}`;
    const session = localStorage.getItem(sessionKey);
    if (session) {
      try {
        const sessionData = JSON.parse(session);
        return !!sessionData.session_token;
      } catch (error) {
        localStorage.removeItem(sessionKey);
        return false;
      }
    }
    return false;
  };

  // Load event data
  useEffect(() => {
    const loadEvent = async () => {
      try {
        setLoading(true);
        setError(null);

        const eventSlug = getEventSlug();

        if (eventSlug) {
          const isAuth = isAuthenticated();

          // Use authenticated API if user is logged in, otherwise use public API
          const api = isAuth ? authenticatedApi : publicApi;

          const eventData = await api.getEvent(eventSlug);

          setEvent(eventData);

          // Update favicon with event logo
          updateFaviconWithEventLogoOrText(eventData.logo_url, eventData.name);

          // Update manifest for event-specific PWA
          const url = updateManifest(eventData);
          setManifestURL(url);
        } else {
          setEvent(null);
          // Reset favicon to default
          resetFavicon();
          // Clean up manifest if no event
          if (manifestURL) {
            cleanupManifest(manifestURL);
            setManifestURL(null);
          }
        }
      } catch (err) {
        setError(err.message || "Failed to load event data");
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, []);

  // Reload event data when authentication state changes
  useEffect(() => {
    const handleStorageChange = () => {
      const eventSlug = getEventSlug();
      if (eventSlug) {
        const loadEvent = async () => {
          try {
            setLoading(true);
            setError(null);

            const isAuth = isAuthenticated();

            // Use authenticated API if user is logged in, otherwise use public API
            const api = isAuth ? authenticatedApi : publicApi;

            const eventData = await api.getEvent(eventSlug);

            setEvent(eventData);

            // Update favicon with event logo
            updateFaviconWithEventLogoOrText(
              eventData.logo_url,
              eventData.name
            );

            // Update manifest for event-specific PWA
            const url = updateManifest(eventData);
            setManifestURL(url);
          } catch (err) {
            setError(err.message || "Failed to reload event data");
            setEvent(null);
          } finally {
            setLoading(false);
          }
        };

        loadEvent();
      }
    };

    // Listen for changes to localStorage (authentication state changes)
    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events that might indicate auth state changes
    window.addEventListener("attendee-authenticated", handleStorageChange);
    window.addEventListener("attendee-updated", handleStorageChange);
    window.addEventListener("attendee_session_updated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("attendee-authenticated", handleStorageChange);
      window.removeEventListener("attendee-updated", handleStorageChange);
      window.removeEventListener(
        "attendee_session_updated",
        handleStorageChange
      );
    };
  }, []);

  // Cleanup manifest URL on unmount
  useEffect(() => {
    return () => {
      if (manifestURL) {
        cleanupManifest(manifestURL);
      }
    };
  }, [manifestURL]);

  // Format date for display
  const formatEventDate = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "";
    }
  };

  // Format time for display
  const formatEventTime = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "";
    }
  };

  // Get event logo URL
  const getEventLogoUrl = () => {
    if (!event?.logo_url) return null;
    return event.logo_url;
  };

  // Get event name
  const getEventName = () => {
    return event?.name || "Event";
  };

  const getEventDateRange = () => {
    if (!event?.start_time || !event?.end_time) return "";

    try {
      // Try parsing dates as-is first (works for most browsers)
      let start = new Date(event.start_time);
      let end = new Date(event.end_time);

      // If dates are invalid, try iOS Safari compatible parsing
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        // For iOS Safari, try adding timezone info if missing
        let startTime = event.start_time;
        let endTime = event.end_time;

        if (
          typeof startTime === "string" &&
          !startTime.includes("Z") &&
          !startTime.includes("+") &&
          !startTime.includes("-", 10)
        ) {
          startTime += "Z";
        }
        if (
          typeof endTime === "string" &&
          !endTime.includes("Z") &&
          !endTime.includes("+") &&
          !endTime.includes("-", 10)
        ) {
          endTime += "Z";
        }

        start = new Date(startTime);
        end = new Date(endTime);

        // If still invalid, return empty string
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          return "";
        }
      }

      const startMonth = start.toLocaleDateString("en-US", { month: "long" });
      const endMonth = end.toLocaleDateString("en-US", { month: "long" });
      const startDay = start.getDate();
      const endDay = end.getDate();
      const startYear = start.getFullYear();
      const endYear = end.getFullYear();

      if (
        startDay === endDay &&
        startMonth === endMonth &&
        startYear === endYear
      ) {
        return `${startMonth} ${startDay}, ${startYear}`;
      }

      if (startMonth === endMonth && startYear === endYear) {
        return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
      }

      if (startYear === endYear) {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
      }

      return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
    } catch (error) {
      return "";
    }
  };

  const value = {
    event,
    loading,
    error,
    getEventSlug,
    getEventLogoUrl,
    getEventName,
    getEventDateRange,
    formatEventDate,
    formatEventTime,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
}

export function useEvent() {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error("useEvent must be used within an EventProvider");
  }
  return context;
}

// Safe version of useEvent that doesn't throw an error
export function useEventSafe() {
  const context = useContext(EventContext);
  return context || { event: null, loading: true, error: null };
}
